/**
 * @author      joseph.murphy@zumper.com (murph)
 * @copyright   Copyright (c) 2022, Zumper
 */

// https://chakra-ui.com/docs/styled-system/theming/component-style#styling-single-part-components

import { formAnatomy } from '@chakra-ui/anatomy'
import { PartsStyleFunction, mode } from '@chakra-ui/theme-tools'
import type { SystemStyleObject } from '@chakra-ui/theme-tools'

import { focus } from '../sharedStyles/focus'

const input: SystemStyleObject = {
  fontSize: 'md',
  pb: 2,
  pt: 4,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'gray.200',
  background: 'white',
  height: '51px',
}

const inputInvalid: SystemStyleObject = {
  input: {
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: 'red.500',
  },
}

const label: SystemStyleObject = {
  top: 2,
  left: 0,
  zIndex: 2,
  fontSize: 'md',
  position: 'absolute',
  color: 'blackAlpha.700',
  pointerEvents: 'none',
  mx: 3,
  px: 1,
  my: 2,
  transformOrigin: 'left top',
}

const labelActive: SystemStyleObject = {
  transform: 'translateY(-12px)',
  fontSize: 'xs',
}

const baseStyle: PartsStyleFunction<typeof formAnatomy> = (props) => ({
  requiredIndicator: {
    color: 'blackAlpha.700',
  },
  helperText: {
    color: mode('blackAlpha.700', 'white')(props),
  },
})

const variantStaticLabel: PartsStyleFunction<typeof formAnatomy> = (props) => ({
  container: {
    input,
    label: {
      ...label,
      ...labelActive,
    },
    _focusWithin: {
      input: focus,
      label: labelActive,
    },
    _invalid: inputInvalid,
  },
})

const variantFloatingLabel: PartsStyleFunction<typeof formAnatomy> = (props) => ({
  container: {
    input,
    label,
    _focusWithin: {
      input: focus,
      label: labelActive,
    },
    _invalid: inputInvalid,
    'input:not(:placeholder-shown) + label, .chakra-select__wrapper + label, textarea:not(:placeholder-shown) ~ label':
      {
        ...labelActive,
      },
  },
  requiredIndicator: {
    marginStart: 0,
  },
})

const variants = {
  floatingLabel: variantFloatingLabel,
  staticLabel: variantStaticLabel,
}

export const Z4Form = {
  parts: formAnatomy.keys,
  defaultProps: {
    variant: 'floatingLabel',
  },
  baseStyle,
  variants,
}
